import { gql } from '@apollo/client';
import React from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import EventsSynchronizer from '~Components/EventsSynchronizer';
import { useDispatch, useLogger } from '~Hooks';
import {
  AddOrderStatusEvents,
  AddOrderStatusEventsVariables,
  GetOrderStatusEvents,
  GetOrderStatusEventsVariables,
  GetOrderStatusSnapshot,
  GetOrderStatusSnapshotVariables,
} from './__queries__';
import { useSocket } from '~Components/SocketManager';

const ORDER_STATUS_SNAPSHOT_QUERY = gql`
  query GetOrderStatusSnapshot($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      eventOrderStatus {
        snapshot
      }
    }
  }
`;

const ORDER_STATUS_EVENTS_QUERY = gql`
  query GetOrderStatusEvents($venueId: ID!, $sentAfter: DateTime!) {
    venue: Venue(id: $venueId) {
      id
      eventOrderStatus {
        events(sentAfter: $sentAfter) {
          lastKey
          items
        }
      }
    }
  }
`;

const ADD_ORDER_STATUS_EVENT_MUTATION = gql`
  mutation AddOrderStatusEvents($venueId: String!, $events: Json!) {
    addOrderStatusEvents(venueId: $venueId, events: $events) {
      success
      message
    }
  }
`;

interface Props {
  venueId: string;
}

const OrderStatusSynchronizer: React.FunctionComponent<Props> = ({ venueId }) => {
  const client = useApolloClient();
  const socket = useSocket();
  const dispatch = useDispatch();

  const [addOrderStatusEvents] = useMutation<AddOrderStatusEvents, AddOrderStatusEventsVariables>(
    ADD_ORDER_STATUS_EVENT_MUTATION,
  );

  return (
    <EventsSynchronizer
      venueId={venueId}
      eventType="ADD_ORDER_STATUS_EVENT"
      manyEventsType="ADD_ORDER_STATUS_EVENTS"
      snapshotType="SET_ORDER_STATUS_SNAPSHOT"
      eventSelector={(state) => state.orderStatusEvents}
      snapshotSelector={(state) => state.orderStatusSnapshot}
      pollingInterval={6500}
      /* Polling query for new events */
      queryEvents={async (timestamp) => {
        const response = await client.query<GetOrderStatusEvents, GetOrderStatusEventsVariables>({
          query: ORDER_STATUS_EVENTS_QUERY,
          variables: {
            venueId,
            sentAfter: timestamp,
          },
          fetchPolicy: 'network-only',
        });

        const events = response.data?.venue?.eventOrderStatus?.events?.items;
        const success = Boolean(events);
        /**
         * Register that we actually got data.
         * Doesn't matter if we got new items/events or not, just that we have
         * established a connection with the backend and it sent us data
         *
         * If this action isnt triggered in a while, we will show a warning
         */
        if (success) {
          dispatch({
            type: 'NEW_ORDER_STATUS_DATA',
          });
        }
        return {
          success,
          events: events,
          error: (response.errors && response.errors[0].message) || null,
        };
      }}
      /* Snapshot query */
      querySnapshot={async () => {
        const response = await client.query<
          GetOrderStatusSnapshot,
          GetOrderStatusSnapshotVariables
        >({
          query: ORDER_STATUS_SNAPSHOT_QUERY,
          variables: {
            venueId,
          },
          fetchPolicy: 'network-only',
        });

        const snapshot = response.data?.venue?.eventOrderStatus?.snapshot;

        return {
          success: Boolean(snapshot),
          snapshot: snapshot || null,
          timestamp: snapshot?.date || null,
          error: (response.errors && response.errors[0].message) || null,
        };
      }}
      /* Send event mutation */
      addEventMutation={async (events) => {
        const response = await addOrderStatusEvents({
          variables: {
            venueId,
            events,
          },
        });

        if (!response?.data?.addOrderStatusEvents?.success) {
          return {
            success: false,
            error: response.errors?.map((error) => error.message).join(', ') || 'Unknown',
          };
        }

        return {
          error: null,
          success: true,
        };
      }}
      sendSocketMessage={(event) => socket.send.orderStatusEvent(event)}
    />
  );
};

export default OrderStatusSynchronizer;
